<script setup lang="ts">
import type { ApexOptions } from 'apexcharts'
import { computed, onMounted, ref } from 'vue'
import { useTheme } from 'vuetify'
import {
  type ErrorCategory,
  type ErrorStatItem,
  errorStatsApi,
  getCategoryColor,
  getCategoryLabel,
  getErrorCategory,
  getErrorCodeLabel,
} from '@/api/entities/review/errorStats'
import { hexToRgb } from '@core/utils/colorConverter'

definePage({
  meta: {
    title: '실패 원인 통계',
    action: 'read',
    subject: 'Upload',
  },
})

const { handleError } = useErrorHandler()
const vuetifyTheme = useTheme()

// 상태
const loading = ref(false)
const stats = ref<ErrorStatItem[]>([])

// 테마 색상
const currentTheme = computed(() => vuetifyTheme.current.value.colors)

// 카테고리별 집계
const categoryStats = computed(() => {
  const result: Record<ErrorCategory, number> = {
    collection: 0,
    transform: 0,
    upload: 0,
    unknown: 0,
  }

  stats.value.forEach(item => {
    const cat = getErrorCategory(item.error_code)

    result[cat] += item.count
  })

  return result
})

// 총 에러 수
const totalErrors = computed(() =>
  Object.values(categoryStats.value).reduce((a, b) => a + b, 0),
)

// Donut Chart 시리즈
const donutChartSeries = computed(() => [
  categoryStats.value.collection,
  categoryStats.value.transform,
  categoryStats.value.upload,
  categoryStats.value.unknown,
])

// Donut Chart 옵션
const donutChartOptions = computed<ApexOptions>(() => {
  const themeSecondaryTextColor = `rgba(${hexToRgb(currentTheme.value['on-surface'])},0.6)`
  const themePrimaryTextColor = `rgba(${hexToRgb(currentTheme.value['on-surface'])},0.87)`

  return {
    chart: {
      type: 'donut',
    },
    stroke: { width: 0 },
    labels: ['수집', '변환', '업로드', '기타'],
    colors: [
      currentTheme.value.warning,
      currentTheme.value.info,
      currentTheme.value.error,
      currentTheme.value.secondary,
    ],
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${Math.round(val)}%`,
    },
    legend: {
      position: 'bottom',
      markers: { offsetX: -3 },
      fontSize: '13px',
      labels: { colors: themeSecondaryTextColor },
      itemMargin: {
        vertical: 3,
        horizontal: 10,
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            name: {
              fontSize: '1rem',
            },
            value: {
              fontSize: '1.25rem',
              color: themeSecondaryTextColor,
              formatter: (val: string) => `${Number.parseInt(val, 10)}건`,
            },
            total: {
              show: true,
              fontSize: '1rem',
              label: '총 에러',
              formatter: () => `${formatNumber(totalErrors.value)}건`,
              color: themePrimaryTextColor,
            },
          },
        },
      },
    },
    responsive: [
      {
        breakpoint: 992,
        options: {
          chart: { height: 350 },
        },
      },
      {
        breakpoint: 576,
        options: {
          chart: { height: 300 },
        },
      },
    ],
  }
})

// Bar Chart용 Top 10 에러 코드
const topErrorCodes = computed(() => {
  return [...stats.value]
    .sort((a, b) => b.count - a.count)
    .slice(0, 10)
})

// Bar Chart 시리즈
const barChartSeries = computed(() => [{
  name: '건수',
  data: topErrorCodes.value.map(item => item.count),
}])

// Bar Chart 옵션
const barChartOptions = computed<ApexOptions>(() => {
  const themeDisabledTextColor = `rgba(${hexToRgb(currentTheme.value['on-surface'])},0.38)`
  const themeBorderColor = `rgba(${hexToRgb(currentTheme.value['on-surface'])},0.12)`

  return {
    chart: {
      type: 'bar',
      parentHeightOffset: 0,
      toolbar: { show: false },
    },
    colors: [currentTheme.value.primary],
    dataLabels: { enabled: false },
    plotOptions: {
      bar: {
        borderRadius: 6,
        barHeight: '70%',
        horizontal: true,
      },
    },
    grid: {
      borderColor: themeBorderColor,
      xaxis: { lines: { show: false } },
      padding: { top: -10 },
    },
    yaxis: {
      labels: {
        style: { colors: themeDisabledTextColor, fontSize: '0.75rem' },
        formatter: (val: string | number) => getErrorCodeLabel(val),
      },
    },
    xaxis: {
      axisBorder: { show: false },
      axisTicks: { color: themeBorderColor },
      categories: topErrorCodes.value.map(item => item.error_code),
      labels: {
        style: { colors: themeDisabledTextColor, fontSize: '0.75rem' },
      },
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${formatNumber(val)}건`,
      },
    },
  }
})

// 테이블 헤더
const headers = [
  { title: '에러 코드', key: 'error_code', sortable: true },
  { title: '카테고리', key: 'category', sortable: true },
  { title: '건수', key: 'count', sortable: true, align: 'end' as const },
  { title: '최근 발생', key: 'latest', sortable: true },
]

// 테이블 데이터 (카테고리 추가)
const tableItems = computed(() => {
  return stats.value.map(item => ({
    ...item,
    category: getErrorCategory(item.error_code),
  }))
})

// 숫자 포맷
const formatNumber = (num: number | null | undefined) => {
  if (num === null || num === undefined)
    return '0'

  return new Intl.NumberFormat('ko-KR').format(num)
}

// 날짜 포맷
const formatDate = (dateString: string | null) => {
  if (!dateString)
    return '-'

  const date = new Date(dateString)

  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

// 데이터 로드
const loadStats = async () => {
  loading.value = true
  try {
    const { success, data } = await errorStatsApi.getErrorStats()
    if (success && data?.stats)
      stats.value = data.stats
  }
  catch (error) {
    handleError(error, { showNotification: false })
  }
  finally {
    loading.value = false
  }
}

onMounted(loadStats)
</script>

<template>
  <div>
    <!-- 헤더 -->
    <VCard class="mb-4">
      <VCardText class="d-flex justify-space-between align-center">
        <div>
          <h5 class="text-h5 mb-1">
            실패 원인별 통계
          </h5>
          <span class="text-body-2 text-medium-emphasis">최근 7일간 발생한 에러 통계</span>
        </div>
        <VBtn
          color="primary"
          :loading="loading"
          @click="loadStats"
        >
          <VIcon
            start
            icon="tabler-refresh"
          />
          새로고침
        </VBtn>
      </VCardText>
    </VCard>

    <!-- Summary Cards -->
    <VRow class="mb-4">
      <VCol
        cols="12"
        sm="6"
        md="3"
      >
        <VCard>
          <VCardText class="d-flex align-center">
            <VAvatar
              size="44"
              color="primary"
              variant="tonal"
              rounded
              class="me-3"
            >
              <VIcon
                icon="tabler-alert-circle"
                size="26"
              />
            </VAvatar>
            <div>
              <p class="text-caption text-medium-emphasis mb-0">
                총 에러
              </p>
              <h5 class="text-h5">
                {{ formatNumber(totalErrors) }}
              </h5>
            </div>
          </VCardText>
        </VCard>
      </VCol>
      <VCol
        cols="12"
        sm="6"
        md="3"
      >
        <VCard>
          <VCardText class="d-flex align-center">
            <VAvatar
              size="44"
              color="warning"
              variant="tonal"
              rounded
              class="me-3"
            >
              <VIcon
                icon="tabler-download"
                size="26"
              />
            </VAvatar>
            <div>
              <p class="text-caption text-medium-emphasis mb-0">
                수집 오류
              </p>
              <h5 class="text-h5 text-warning">
                {{ formatNumber(categoryStats.collection) }}
              </h5>
            </div>
          </VCardText>
        </VCard>
      </VCol>
      <VCol
        cols="12"
        sm="6"
        md="3"
      >
        <VCard>
          <VCardText class="d-flex align-center">
            <VAvatar
              size="44"
              color="info"
              variant="tonal"
              rounded
              class="me-3"
            >
              <VIcon
                icon="tabler-transform"
                size="26"
              />
            </VAvatar>
            <div>
              <p class="text-caption text-medium-emphasis mb-0">
                변환 오류
              </p>
              <h5 class="text-h5 text-info">
                {{ formatNumber(categoryStats.transform) }}
              </h5>
            </div>
          </VCardText>
        </VCard>
      </VCol>
      <VCol
        cols="12"
        sm="6"
        md="3"
      >
        <VCard>
          <VCardText class="d-flex align-center">
            <VAvatar
              size="44"
              color="error"
              variant="tonal"
              rounded
              class="me-3"
            >
              <VIcon
                icon="tabler-upload"
                size="26"
              />
            </VAvatar>
            <div>
              <p class="text-caption text-medium-emphasis mb-0">
                업로드 오류
              </p>
              <h5 class="text-h5 text-error">
                {{ formatNumber(categoryStats.upload) }}
              </h5>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Charts -->
    <VRow class="mb-4">
      <!-- Donut Chart: 카테고리별 비율 -->
      <VCol
        cols="12"
        md="5"
      >
        <VCard>
          <VCardTitle>카테고리별 분포</VCardTitle>
          <VCardText>
            <VueApexCharts
              v-if="totalErrors > 0"
              type="donut"
              :options="donutChartOptions"
              :series="donutChartSeries"
              :height="350"
            />
            <div
              v-else
              class="d-flex justify-center align-center"
              style="height: 350px"
            >
              <span class="text-medium-emphasis">데이터가 없습니다</span>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Bar Chart: 에러코드별 Top 10 -->
      <VCol
        cols="12"
        md="7"
      >
        <VCard>
          <VCardTitle>에러 코드별 Top 10</VCardTitle>
          <VCardText>
            <VueApexCharts
              v-if="topErrorCodes.length > 0"
              type="bar"
              :options="barChartOptions"
              :series="barChartSeries"
              :height="350"
            />
            <div
              v-else
              class="d-flex justify-center align-center"
              style="height: 350px"
            >
              <span class="text-medium-emphasis">데이터가 없습니다</span>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Error Code Table -->
    <VCard>
      <VCardTitle>에러 코드별 상세</VCardTitle>
      <VCardText>
        <VDataTable
          :headers="headers"
          :items="tableItems"
          :loading="loading"
          :sort-by="[{ key: 'count', order: 'desc' }]"
          class="elevation-0"
        >
          <template #item.error_code="{ item }">
            <div>
              <div class="font-weight-medium">
                {{ getErrorCodeLabel(item.error_code) }}
              </div>
              <div class="text-caption text-medium-emphasis">
                {{ item.error_code }}
              </div>
            </div>
          </template>
          <template #item.category="{ item }">
            <VChip
              :color="getCategoryColor(item.category)"
              size="small"
              variant="tonal"
            >
              {{ getCategoryLabel(item.category) }}
            </VChip>
          </template>
          <template #item.count="{ item }">
            <span class="font-weight-medium">{{ formatNumber(item.count) }}</span>
          </template>
          <template #item.latest="{ item }">
            {{ formatDate(item.latest) }}
          </template>
        </VDataTable>
      </VCardText>
    </VCard>
  </div>
</template>
